import React, { FC, useEffect, useMemo, useState } from "react";
import {
  Empty,
  Input,
  Modal,
  Select,
  Table,
  Tooltip,
  Typography,
  Tag,
} from "antd";
import DocumentUploadBillAuditStage from "./DocumentUploadBillAuditStage";
import { Spin } from "antd";
import { Button } from "antd";
import { API_URL, ROUTES } from "../../../utils/constants";
import { openBillAuditForMapping } from "../../../utils/billAuditUrlUtils";
import { isMidOfficeRole } from "../../../utils/roleUtils";
import { useUserFunctionalities } from "../../../utils/UserFunctionalities";
import { RootState } from "../../../store/store";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { errorMessage } from "../../../utils/notificationUtils";
import { getGovtBillList } from "../../../apis/govtBillingService";
import ReconBillUploadContainer from "../../../containers/patient/BillAuditStage/ReconBillUploadContainer";
import { ReconBillUploadRow } from "./ReconBillUploadTable";
import { DsAiRead } from "../../../apis/reconBillUploadService";
import {
  getCghsPayableBreakdown,
  getCpdRemarks,
} from "../../../apis/patientService";

const { Text } = Typography;

interface Document {
  id: number;
  type?: string;
  file?: any;
  documentId?: any;
  label?: string;
  labelId?: string | number;
  documentName?: string;
  requestDocument?: boolean;
  responseDocument?: boolean;
}

interface DocumentUrl {
  documentType: string;
  documentName: string;
  documentUrl: string;
  id: number;
  referenceType: string | null;
  documentLabelType: any;
  deficiencyCheckStatus: any;
  remarks: string | null;
}

const { Title } = Typography;

interface BillAuditStageProps {
  isLoading: boolean;
  documentTypes: Document[];
  documentUrls: DocumentUrl[];
  handleFileChange: (
    documentType: string,
    isMultiple: boolean,
  ) => (e: any) => void;
  handleOpen: any;
  handleSaveData: () => void;
  uploadFileList: any[];
  disabled: any;
  isUploading: any;
  handleSaveBillData?: any;
  roomTypeOptions: any;
  hospitalRoomTypeOptions: any;
  selectedRoomType: any;
  selectedHospitalRoomType: any;
  roomTypeError: any;
  hospitalRoomTypeError: any;
  setRoomTypeError: any;
  setHospitalRoomTypeError: any;
  handleRoomTypeChange: any;
  handleHospitalRoomTypeChange: any;
  isTPA: any;
  notMatchedError: any;
  notMatchedHospitalError: any;
  handleTriggerBillAudit: any;
  isBillAuditTriggered: boolean;
  billSummaryLink: any;
  isFinalBillUploaded: any;
  fetchGovtBill: any;
  isFetchingBillDetails: boolean;
  hasReconBillRows?: boolean;
  isReconBillAuditBlocked?: boolean;
  reconBillAuditBlockMessage?: string;
  reconBillUploadRows?: ReconBillUploadRow[];
  reconBillUploadLoading?: boolean;
  reconDsRead?: DsAiRead | null;
  refetchReconBillUploadRows?: () => Promise<void>;
  reconHospitalId?: number;
  reconSchemeId?: number;
  reconBillMappingId?: number;
  onReconBillDocumentAttached?: (uploadedDocs: any[]) => Promise<void>;
  onReconBillDocumentDeleted?: (documentUrl: string) => void;
  schemeMetaDataName?: string;
}

type PayableBreakdownItem = {
  billNo?: string;
  date?: string;
  name?: string;
  category?: string;
  qty?: number;
  rate?: number;
  total?: number;
  code?: string;
  cghsRate?: number;
  cghsAmount?: number;
  variation?: number;
};

type PayableBreakdownResponse = {
  totals?: {
    hospitalTotal?: number;
    cghsTotal?: number;
    patientPayableTotal?: number;
    nonPayableTotal?: number;
  };
  items?: {
    patientPayableItems?: PayableBreakdownItem[];
    nonPayableItems?: PayableBreakdownItem[];
    remainingItems?: PayableBreakdownItem[];
  };
};

type PayableBreakdownModalKey =
  | "hospitalTotal"
  | "cghsTotal"
  | "patientPayableTotal"
  | "nonPayableTotal";

const breakdownCardLabels: Record<PayableBreakdownModalKey, string> = {
  hospitalTotal: "Hospital Total",
  cghsTotal: "CGHS Total",
  patientPayableTotal: "Patient Payable Total",
  nonPayableTotal: "Non-Payable Total",
};

const formatCurrencyAmount = (value?: number | string | null) => {
  const numericValue = Number(value ?? 0);
  if (Number.isNaN(numericValue)) return "0.00";
  return numericValue.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const BillAuditStage: FC<BillAuditStageProps> = ({
  isLoading,
  documentTypes,
  documentUrls,
  handleFileChange,
  handleOpen,
  handleSaveData,
  uploadFileList,
  disabled,
  isUploading,
  handleSaveBillData,
  roomTypeOptions,
  hospitalRoomTypeOptions,
  selectedRoomType,
  selectedHospitalRoomType,
  roomTypeError,
  hospitalRoomTypeError,
  setRoomTypeError,
  setHospitalRoomTypeError,
  handleRoomTypeChange,
  handleHospitalRoomTypeChange,
  isTPA,
  notMatchedError,
  notMatchedHospitalError,
  handleTriggerBillAudit,
  isBillAuditTriggered,
  billSummaryLink,
  isFinalBillUploaded,
  fetchGovtBill,
  isFetchingBillDetails,
  hasReconBillRows = false,
  isReconBillAuditBlocked = false,
  reconBillAuditBlockMessage = "",
  reconBillUploadRows = [],
  reconBillUploadLoading = false,
  reconDsRead = null,
  refetchReconBillUploadRows,
  reconHospitalId,
  reconSchemeId,
  reconBillMappingId,
  onReconBillDocumentAttached,
  onReconBillDocumentDeleted,
  schemeMetaDataName,
}) => {
  const { hasFunctionality } = useUserFunctionalities();
  const { patients, preAuth, auth } = useSelector((state: RootState) => state);

  const [billAuditStageStatus, setBillAuditStageStatus] = useState<any>(false);
  const [billAuditSummaryGenerated, setBillAuditSummaryGenerated] =
    useState<any>(false);
  const [isBillAuditIntimated, setIsBillAuditIntimated] =
    useState<boolean>(false);
  const [hasReconMismatch, setHasReconMismatch] = useState(false);
  const [isFinalBillStatus, setIsFinalBillStatus] = useState<boolean>(false);
  const isReconBillUploadLocked =
    billAuditSummaryGenerated || isFinalBillStatus;

  // Register-mandate block does not apply once Audited / Summary Generated.
  const isBillAuditActionBlocked =
    disabled || (isReconBillAuditBlocked && !billAuditSummaryGenerated);

  const visitType = patients?.fetchBasicDetails?.visitInfoResponse?.visitType;
  const hideDocumentUpload =
    (visitType?.toUpperCase() === "OP" ||
      visitType?.toUpperCase() === "IP" ||
      visitType?.toUpperCase() === "EMERGENCY") &&
    schemeMetaDataName?.toUpperCase() === "CGHS NEW" &&
    hasReconBillRows;
  const [payableBreakdown, setPayableBreakdown] =
    useState<PayableBreakdownResponse | null>(null);
  const [payableBreakdownLoading, setPayableBreakdownLoading] =
    useState<boolean>(false);
  const [payableBreakdownError, setPayableBreakdownError] =
    useState<string>("");
  const [breakdownModalKey, setBreakdownModalKey] =
    useState<PayableBreakdownModalKey | null>(null);
  const [breakdownPagination, setBreakdownPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [cpdRemarksData, setCpdRemarksData] = useState<{
    approved?: boolean;
    cpd_remarks?: string;
  } | null>(null);
  const [cpdRemarksLoading, setCpdRemarksLoading] = useState<boolean>(false);
  const [hideSendToQc, setHideSendToQc] = useState<boolean>(false);

  useEffect(() => {
    const billAudit = preAuth?.stepperData?.visitStages?.find(
      (visit) => visit?.visitStage?.toUpperCase() === "BILL AUDIT",
    );
    const currentState = String(billAudit?.state || "")
      .trim()
      .replace(/_/g, " ")
      .replace(/\s+/g, " ")
      .toUpperCase();
    setHideSendToQc(
      ["REVIEW REQUESTED", "ACCEPTED", "REJECTED", "PARTIAL ACCEPTED"].includes(
        currentState,
      ),
    );
    setBillAuditStageStatus(
      (billAudit?.intimated ||
        billAudit?.state === "Bill Uploaded" ||
        billAudit?.state?.toUpperCase() ===
          "Final Bill Added"?.toUpperCase()) &&
        billAudit?.state?.toLowerCase() !== "query replied",
    );
    setIsBillAuditIntimated(
      billAudit?.intimated || billAudit?.state === "Bill Uploaded",
    );

    setBillAuditSummaryGenerated(
      billAudit?.completed ||
        billAudit?.state === "Summary Generated" ||
        billAudit?.state === "Audited",
    );

    setIsFinalBillStatus(
      billAudit?.state?.toUpperCase() === "Final Bill Added"?.toUpperCase(),
    );
  }, [preAuth?.stepperData?.visitStages]);

  useEffect(() => {
    setBreakdownPagination((prev) => ({
      ...prev,
      current: 1,
    }));
  }, [breakdownModalKey]);

  const [govtBillMappingId, setGovtBillMappingId] = useState<any>(null);
  const [govtBillMappingStatus, setGovtBillMappingStatus] = useState<any>(null);

  const [isHospitalBillUploaded, setIsHospitalBillUploaded] =
    useState<boolean>(false);
  const [isFinalBillPresent, setIsFinalBillPresent] = useState<boolean>(false);
  const isReconBillDocumentUploaded =
    hasReconBillRows &&
    (() => {
      const activeBillRows =
        reconBillUploadRows?.filter(
          (row) =>
            row.documentKind !== "DS" &&
            row.documentKind !== "OTHERS" &&
            !row.cancelled,
        ) || [];
      if (activeBillRows.length === 0) {
        return false;
      }
      const allBillsUploaded = activeBillRows.every((row) => !!row.documentId);
      const visit = visitType?.toUpperCase();
      const needsDs = visit === "IP" || visit === "EMERGENCY";
      if (!needsDs) {
        return allBillsUploaded;
      }
      const hasDsUploaded = documentUrls?.some(
        (doc) => doc?.documentLabelType?.toUpperCase() === "DISCHARGE SUMMARY",
      );
      return allBillsUploaded && !!hasDsUploaded;
    })();
  const canSendToQc = isHospitalBillUploaded || isReconBillDocumentUploaded;

  useEffect(() => {
    if (!hasReconBillRows) {
      setHasReconMismatch(false);
    }
  }, [hasReconBillRows]);

  const handleSendToQcClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!patients?.fetchBasicDetails?.visitInfoResponse?.id) {
      return;
    }
    if (hasReconBillRows && hasReconMismatch) {
      handleTriggerBillAudit("intimateMismatch");
      return;
    }
    handleTriggerBillAudit("intimate");
  };

  useEffect(() => {
    const isBill = documentUrls?.some(
      (docType) =>
        docType?.documentLabelType?.toUpperCase() === "HOSPITAL BILL",
    );
    const isFinalBill = documentUrls?.some(
      (doc) => doc?.documentLabelType?.toUpperCase() === "FINAL BILL",
    );

    setIsHospitalBillUploaded(isBill);
    setIsFinalBillPresent(isFinalBill);
  }, [documentUrls]);

  const fetchHospitalBillList = async () => {
    try {
      if (patients?.fetchBasicDetails?.casesResponse?.healthCareId) {
        const filters = {
          pageNumber: 1,
          pageSize: 10,
          searchTerm: "",
        };
        const response = await getGovtBillList(
          patients?.fetchBasicDetails?.casesResponse?.healthCareId,
          patients?.fetchBasicDetails?.casesResponse?.id,
          patients?.fetchBasicDetails?.visitInfoResponse?.id,
          filters,
        );

        setGovtBillMappingId(
          response?.data?.billMappingResponseList?.length
            ? response.data.billMappingResponseList?.[0].id
            : null,
        );
        setGovtBillMappingStatus(
          response?.data?.billMappingResponseList?.length
            ? response.data.billMappingResponseList?.[0].status
            : null,
        );
      }
    } catch (err) {
      setGovtBillMappingId(null);
    }
  };

  useEffect(() => {
    // fetchHospitalBillList();
    setGovtBillMappingId(
      patients?.fetchBasicDetails?.visitInfoResponse?.billMappingId,
    );
    setGovtBillMappingStatus(
      patients?.fetchBasicDetails?.visitInfoResponse?.billMappingStatus,
    );
  }, [patients?.fetchBasicDetails]);

  const extractStepStatus = (rawData: string) => {
    try {
      const parsed = JSON.parse(rawData);
      const dataPayload =
        parsed.data && typeof parsed.data === "object" ? parsed.data : {};
      return {
        ...parsed,
        ...dataPayload,
        stepName: parsed.stepName || parsed.step_name || "BILL_AUDIT",
        status: parsed.status || parsed.current_status || "",
      };
    } catch {
      try {
        const normalized = rawData.replace(/'/g, '"');
        const parsed = JSON.parse(normalized);
        const dataPayload =
          parsed.data && typeof parsed.data === "object" ? parsed.data : {};
        return {
          ...parsed,
          ...dataPayload,
          stepName: parsed.stepName || parsed.step_name || "BILL_AUDIT",
          status: parsed.status || parsed.current_status || "",
        };
      } catch {
        return null;
      }
    }
  };

  const [sseDataMap, setSseDataMap] = useState<Record<string, any>>({});

  const getSseStatusColor = (status: string) => {
    if (!status) return "default";
    const normalized = status.toString().toLowerCase();
    if (normalized.includes("success") || normalized.includes("completed"))
      return "green";
    if (
      normalized.includes("in_progress") ||
      normalized.includes("in progress") ||
      normalized.includes("processing")
    )
      return "blue";
    if (normalized.includes("pending") || normalized.includes("waiting"))
      return "orange";
    if (normalized.includes("failed") || normalized.includes("error"))
      return "red";
    return "blue";
  };

  const formatDateTime = (value?: string) => {
    if (!value) return "-";
    let date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    // Convert UTC to IST (UTC+5:30)
    const istDate = new Date(date.getTime() + 5.5 * 60 * 60 * 1000);
    const day = String(istDate.getUTCDate()).padStart(2, "0");
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = monthNames[istDate.getUTCMonth()];
    const year = istDate.getUTCFullYear();
    const hour = istDate.getUTCHours();
    const minute = String(istDate.getUTCMinutes()).padStart(2, "0");
    const second = String(istDate.getUTCSeconds()).padStart(2, "0");
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = String(hour % 12 || 12).padStart(2, "0");
    return `${day} ${month} ${year}, ${displayHour}:${minute}:${second} ${ampm}`;
  };

  const formatDuration = (start?: string, end?: string) => {
    if (!start || !end) return "-";
    const from = new Date(start);
    const to = end ? new Date(end) : new Date();
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return "-";
    const diff = Math.abs(to.getTime() - from.getTime());
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const sseDataCall = [
    // "APPROVED",
    // "BILL_GENERATED",
    "INITIATED",
    // "REQUEST_FOR_APPROVAL",
    // "REJECTED",
    // "READY_FOR_BILL_AUDIT",
    // "INCLUSION_FAILED",
    // "BILL_AUDIT_SUCCESS",

    "UPLOADING_IN_PROGRESS",
    "BILL_READING_IN_PROGRESS",
    "CATEGORIZATION_IN_PROGRESS",
    "NME_MARKING_IN_PROGRESS",
    "HOSPITAL_SOC_FETCHING_IN_PROGRESS",
    "HOSPITAL_SCHEME_TARIFF_FETCHING_IN_PROGRESS",
    "INCLUSION_IDENTIFICATION_IN_PROGRESS",
    "EXCLUSION_IDENTIFICATION_IN_PROGRESS",

    "BILL_READING_PENDING",
    "BILL_READING_IN_PROGRESS",
    "BILL_READING_SUCCESS",
    // "BILL_READING_FAILED",
    // "BILL_READING_PUSHED_TO_OFFLINE_RETRY",
    "CATEGORIZATION_PENDING",
    "CATEGORIZATION_IN_PROGRESS",
    "CATEGORIZATION_SUCCESS",
    // "CATEGORIZATION_FAILED",
    // "CATEGORIZATION_PUSHED_TO_OFFLINE_RETRY",
    "NME_MARKING_PENDING",
    "NME_MARKING_IN_PROGRESS",
    "NME_MARKING_SUCCESS",
    // "NME_MARKING_FAILED"
    // "NME_MARKING_PUSHED_TO_OFFLINE_RETRY"
    "SOC_RATE_FETCHING_PENDING",
    "SOC_RATE_FETCHING_IN_PROGRESS",
    "SOC_RATE_FETCHING_SUCCESS",
    // "SOC_RATE_FETCHING_FAILED"
    // "SOC_RATE_FETCHING_PUSHED_TO_OFFLINE_RETRY"
    "TARIFF_RATE_FETCHING_PENDING",
    "TARIFF_RATE_FETCHING_IN_PROGRESS",
    "TARIFF_RATE_FETCHING_SUCCESS",
    // "TARIFF_RATE_FETCHING_FAILED"
    // "TARIFF_RATE_FETCHING_PUSHED_TO_OFFLINE_RETRY"
    "INCLUSION_RECOMMENDATION_PENDING",
    "INCLUSION_RECOMMENDATION_IN_PROGRESS",
    "INCLUSION_RECOMMENDATION_SUCCESS",
    "EXCLUSION_RECOMMENDATION_PENDING",
    "EXCLUSION_RECOMMENDATION_IN_PROGRESS",
    "EXCLUSION_RECOMMENDATION_SUCCESS",
    "BILL_AUDIT_PENDING",
    "BILL_AUDIT_IN_PROGRESS",
  ];

  useEffect(() => {
    setSseDataMap({});

    if (govtBillMappingId) {
      const eventSources: EventSource[] = [];
      if (
        sseDataCall.includes(govtBillMappingStatus) ||
        govtBillMappingStatus === null
      ) {
        //record?.status
        const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
        const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
        const billMappingId = govtBillMappingId;

        if (caseId && visitId && billMappingId) {
          const url = `${API_URL}/api/v2/notifications/sse/bill/${billMappingId}`;
          const eventSource = new EventSource(url);

          const handleSseMessage = (event: MessageEvent) => {
            const extracted = extractStepStatus(event.data);
            if (!extracted) return;
            setSseDataMap((prev) => ({
              ...prev,
              [billMappingId]: extracted,
            }));
          };

          eventSource.addEventListener("connected", handleSseMessage);
          eventSource.addEventListener("status_update", handleSseMessage);
          eventSource.addEventListener("notification", handleSseMessage);
          eventSource.onmessage = handleSseMessage;

          eventSource.onerror = () => {
            console.error(
              `SSE connection error for billMappingId: ${billMappingId}`,
            );
            eventSource.close();
          };

          eventSources.push(eventSource);
        }
      }

      return () => {
        eventSources.forEach((eventSource) => {
          eventSource.close();
        });
      };
    }
  }, [govtBillMappingId]);

  const currentSseData = sseDataMap[govtBillMappingId] || {};
  const displayBillNo =
    currentSseData.bill_id || currentSseData.bill_mapping_id || "-";
  const displayBillType = preAuth?.portalData?.billType || "-";
  const displayStatus = currentSseData
    ? currentSseData?.status?.toLowerCase() === "success" &&
      currentSseData?.stepName === "BILL_AUDIT"
      ? "Success"
      : currentSseData.status
    : "Pending";
  const displayStart = formatDateTime(currentSseData.processing_started_at);
  const displayProcessing = formatDuration(
    currentSseData.processing_started_at,
    currentSseData.processing_completed_at,
  );

  const billSummaryGenerated = currentSseData
    ? currentSseData?.status?.toLowerCase() === "success" &&
      currentSseData?.stepName === "BILL_SUMMARY"
    : false;

  /** OTHERS upload enabled until Audited or Bill Summary Generated */
  const isOthersUploadLocked = useMemo(() => {
    const billAudit = preAuth?.stepperData?.visitStages?.find(
      (visit) => visit?.visitStage?.toUpperCase() === "BILL AUDIT",
    );
    const state = billAudit?.state?.trim()?.toUpperCase() || "";
    return (
      state === "AUDITED" ||
      state === "SUMMARY GENERATED" ||
      billSummaryGenerated
    );
  }, [preAuth?.stepperData?.visitStages, billSummaryGenerated]);

  // useEffect(() => {
  //   if (
  //     patients?.fetchBasicDetails?.visitInfoResponse?.billMappingId &&
  //     billSummaryGenerated
  //   ) {
  //     fetchGovtBill();
  //   }
  // }, [patients?.fetchBasicDetails?.visitInfoResponse?.billMappingId, billSummaryGenerated]);

  const getReadableStatus = (status?: string, stepName?: string) => {
    if (!status) return "Pending";

    const normalized = status.toLowerCase();

    if (normalized === "success" && stepName === "BILL_AUDIT") {
      return "Bill Audit Completed";
    }

    if (normalized.includes("summary")) {
      return "Bill Summary Generated";
    }

    if (
      normalized.includes("in_progress") ||
      normalized.includes("processing")
    ) {
      return "Processing the Bill";
    }

    if (normalized.includes("pending") && stepName === "BILL_AUDIT") {
      return "Bill audit Pending";
    }

    if (normalized.includes("failed") || normalized.includes("error")) {
      return "Bill Audit Failed";
    }

    return normalized
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const readableStatus = getReadableStatus(
    currentSseData?.status,
    currentSseData?.stepName,
  );

  const isCghsNewScheme =
    patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
    "CGHS NEW";

  useEffect(() => {
    const fetchPayableBreakdown = async () => {
      const billMappingId =
        patients?.fetchBasicDetails?.visitInfoResponse?.billMappingId?.toString();
      const caseId = patients?.fetchBasicDetails?.casesResponse?.id?.toString();
      const visitId =
        patients?.fetchBasicDetails?.visitInfoResponse?.id?.toString();

      if (!billMappingId || !caseId || !visitId) {
        //!isCghsNewScheme ||
        setPayableBreakdown(null);
        setPayableBreakdownError("");
        return;
      }

      try {
        setPayableBreakdownLoading(true);
        setPayableBreakdownError("");
        const response = await getCghsPayableBreakdown({
          billMappingId,
          caseId,
          visitId,
          platform: "G7",
        });
        setPayableBreakdown(response);
      } catch (error: any) {
        setPayableBreakdown(null);
        setPayableBreakdownError(
          error?.message || "Unable to fetch payable breakdown",
        );
      } finally {
        setPayableBreakdownLoading(false);
      }
    };

    const billAudit = preAuth?.stepperData?.visitStages?.find(
      (visit) => visit?.visitStage?.toUpperCase() === "BILL AUDIT",
    );

    if (
      patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase() ===
        "OP" ||
      ((patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase() ===
        "IP" ||
        patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase() ===
          "EMERGENCY") &&
        (
          billAudit?.state === "Summary Generated" ||
          billAudit?.state === "Audited"))
    )
      fetchPayableBreakdown();
  }, [
    isCghsNewScheme,
    patients?.fetchBasicDetails?.casesResponse?.id,
    patients?.fetchBasicDetails?.visitInfoResponse?.billMappingId,
    patients?.fetchBasicDetails?.visitInfoResponse?.id,
  ]);

  useEffect(() => {
    const fetchCpdRemarks = async () => {
      const billMappingId =
        patients?.fetchBasicDetails?.visitInfoResponse?.billMappingId?.toString();
      const caseId = patients?.fetchBasicDetails?.casesResponse?.id?.toString();
      const visitId =
        patients?.fetchBasicDetails?.visitInfoResponse?.id?.toString();

      if (!billMappingId || !caseId || !visitId) {
        //!isCghsNewScheme ||
        setCpdRemarksData(null);
        return;
      }

      try {
        setCpdRemarksLoading(true);
        const response = await getCpdRemarks({
          case_id: caseId,
          visit_id: visitId,
          bill_mapping_id: billMappingId,
          platform: "G7",
        });
        setCpdRemarksData(response);
      } catch {
        setCpdRemarksData(null);
      } finally {
        setCpdRemarksLoading(false);
      }
    };

    fetchCpdRemarks();
  }, [
    isCghsNewScheme,
    patients?.fetchBasicDetails?.casesResponse?.id,
    patients?.fetchBasicDetails?.visitInfoResponse?.billMappingId,
    patients?.fetchBasicDetails?.visitInfoResponse?.id,
  ]);

  const getBreakdownItems = (
    key: PayableBreakdownModalKey,
  ): PayableBreakdownItem[] => {
    const patientPayableItems =
      payableBreakdown?.items?.patientPayableItems || [];
    const nonPayableItems = payableBreakdown?.items?.nonPayableItems || [];
    const remainingItems = payableBreakdown?.items?.remainingItems || [];

    if (key === "hospitalTotal") {
      return [...remainingItems, ...nonPayableItems, ...patientPayableItems];
    }
    if (key === "cghsTotal") {
      return remainingItems;
    }
    if (key === "patientPayableTotal") {
      return patientPayableItems;
    }
    return nonPayableItems;
  };

  const breakdownSummaryCards: Array<{
    key: PayableBreakdownModalKey;
    title: string;
    subtitle: string;
    amount: number;
    itemCount: number;
  }> = [
    {
      key: "hospitalTotal",
      title: "Hospital Total",
      subtitle: "Shows all bill items",
      amount: Number(payableBreakdown?.totals?.hospitalTotal || 0),
      itemCount: getBreakdownItems("hospitalTotal").length,
    },
    {
      key: "cghsTotal",
      title: "CGHS Total",
      subtitle: "Shows Audited Items",
      amount: Number(payableBreakdown?.totals?.cghsTotal || 0),
      itemCount: getBreakdownItems("cghsTotal").length,
    },
    {
      key: "patientPayableTotal",
      title: "Patient Payable Total",
      subtitle: "Shows patient payable items",
      amount: Number(payableBreakdown?.totals?.patientPayableTotal || 0),
      itemCount: getBreakdownItems("patientPayableTotal").length,
    },
    {
      key: "nonPayableTotal",
      title: "Non-Payable Total",
      subtitle: "Shows non-payable items",
      amount: Number(payableBreakdown?.totals?.nonPayableTotal || 0),
      itemCount: getBreakdownItems("nonPayableTotal").length,
    },
  ];

  const activeBreakdownItems = breakdownModalKey
    ? getBreakdownItems(breakdownModalKey)
    : [];

  const paginatedBreakdownItems = activeBreakdownItems.slice(
    (breakdownPagination.current - 1) * breakdownPagination.pageSize,
    breakdownPagination.current * breakdownPagination.pageSize,
  );

  const shouldShowPayableBreakdown =
    // isCghsNewScheme &&
    payableBreakdownLoading ||
    !!payableBreakdownError ||
    breakdownSummaryCards.some(
      (card) => Number(card.amount) > 0 || card.itemCount > 0,
    );

  const cpdRemarksText = (cpdRemarksData?.cpd_remarks || "").trim();
  const shouldShowCpdReview = cpdRemarksLoading || !!cpdRemarksText; //isCghsNewScheme &&

  const SCALE = 0.67;

  const [scrollY, setScrollY] = useState(window.innerHeight / SCALE - 230);

  useEffect(() => {
    const handleResize = () => {
      setScrollY(window.innerHeight / SCALE - 230);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const breakdownColumns = [
    {
      title: "S.No.",
      key: "serialNumber",
      width: 80,
      fixed: "left" as const,
      render: (_: any, __: any, index: number) =>
        (breakdownPagination.current - 1) * breakdownPagination.pageSize +
        index +
        1,
    },
    {
      title: "Bill No",
      dataIndex: "billNo",
      key: "billNo",
      width: 140,
      fixed: "left" as const,
      render: (value: string) => value || "-",
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 130,
      fixed: "left" as const,
      render: (value: string) => value || "-",
    },
    {
      title: "Item Name",
      dataIndex: "name",
      key: "name",
      width: 280,
      render: (value: string) => (
        <div title={value} style={{ whiteSpace: "normal", lineHeight: 1.4 }}>
          {value || "-"}
        </div>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 150,
      render: (value: string) => value || "-",
    },
    {
      title: "Qty",
      dataIndex: "qty",
      key: "qty",
      width: 80,
      align: "right" as const,
      render: (value: number) => value ?? 0,
    },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      width: 120,
      align: "right" as const,
      render: (value: number) => formatCurrencyAmount(value),
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      width: 120,
      align: "right" as const,
      render: (value: number) => formatCurrencyAmount(value),
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      width: 120,
      render: (value: string) => value || "-",
    },
    {
      title: "CGHS Rate",
      dataIndex: "cghsRate",
      key: "cghsRate",
      width: 120,
      align: "right" as const,
      render: (value: number) => formatCurrencyAmount(value),
    },
    {
      title: "CGHS Amount",
      dataIndex: "cghsAmount",
      key: "cghsAmount",
      width: 140,
      align: "right" as const,
      render: (value: number) => formatCurrencyAmount(value),
    },
    {
      title: "Variation",
      dataIndex: "variation",
      key: "variation",
      width: 120,
      align: "right" as const,
      render: (value: number) => {
        const numericValue = Number(value || 0);
        return (
          <span
            style={{
              color:
                numericValue < 0
                  ? "#cf1322"
                  : numericValue > 0
                    ? "#389e0d"
                    : "#1f1f1f",
            }}
          >
            {formatCurrencyAmount(numericValue)}
          </span>
        );
      },
    },
  ];

  return (
    <div>
      {/* <div
        style={{
          background: "linear-gradient(135deg, #f5f7fa 0%, #eef2f9 100%)",
          border: "1px solid #d0d7e3",
          borderRadius: 8,
          padding: "16px 20px",
          marginBottom: 16,
          marginTop: 20,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
        }}
      >
        <div style={{ display: "flex", gap: 36, alignItems: "center", flex: 1 }}>
          <div>
            <div style={{ fontSize: "0.7rem", color: "#666", fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px" }}>Bill ID</div>
            <div style={{ fontSize: "1.05rem", fontWeight: 700, color: "#00549e", fontFamily: "monospace" }}>{displayBillNo}</div>
          </div>

          <div style={{ width: "1px", height: 36, background: "#c5cfe3" }} />

          <div>
            <div style={{ fontSize: "0.7rem", color: "#666", fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px" }}>Status</div>
            <Tag color={getSseStatusColor(displayStatus)} style={{ fontSize: "0.8rem", fontWeight: 600, padding: "4px 12px" }}>
              {displayStatus?.toUpperCase()}
            </Tag>
          </div>

          <div style={{ width: "1px", height: 36, background: "#c5cfe3" }} />

          <div>
            <div style={{ fontSize: "0.7rem", color: "#666", fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px" }}>Processing Time</div>
            <div style={{ fontSize: "0.95rem", fontWeight: 700, color: displayStatus?.toLowerCase() === "success" ? "#52c41a" : "#1890ff" }}>{displayProcessing}</div>
          </div>
        </div>

        <div style={{ display: "flex", gap: "0.8rem", paddingLeft: 20, borderLeft: "1px solid #d0d7e3" }}>
          {!disabled && !billAuditStageStatus && (
            <div style={{ width: "140px" }}>
              <Button
                type="primary"
                style={{
                  backgroundColor: "rgb(0, 76, 143)",
                  color: "#fff",
                  boxShadow: "0px 2px 8px 0px #00000040",
                  width: "100%",
                }}
                onClick={() => {
                  if (isTPA && !selectedRoomType) {
                    setRoomTypeError("Scheme Room Type is required");
                    errorMessage("Scheme Room Type is required");
                    return;
                  }
                  handleSaveData();
                }}
                loading={isUploading}
              >
                SAVE
              </Button>
            </div>
          )}

          {(isMidOfficeRole(auth?.role?.role) || true) && (
            <div style={{ width: "140px" }}>
              <Button
                type="primary"
                style={{
                  backgroundColor: "rgb(0, 76, 143)",
                  color: "#fff",
                  boxShadow: "0px 2px 8px 0px #00000040",
                  width: "100%",
                }}
                onClick={() =>
                  openBillAuditForMapping(
                    govtBillMappingId,
                    patients?.fetchBasicDetails?.casesResponse?.healthCareId,
                    isTPA ? "TPA" : "CGHS",
                  )
                }
              >
                Bill Audit
              </Button>
            </div>
          )}

          {auth?.role?.role === "FO_STAFF" && !billAuditStageStatus && (
            <div style={{ width: "140px" }}>
              <Button
                type="primary"
                style={{
                  backgroundColor: "rgb(0, 76, 143)",
                  color: "#fff",
                  boxShadow: "0px 2px 8px 0px #00000040",
                  width: "100%",
                }}
                onClick={async (e) => {
                  e.stopPropagation();
                  if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
                    handleTriggerBillAudit("intimate");
                  }
                }}
              >
                Send To Qc
              </Button>
            </div>
          )}
        </div>
      </div> */}

      {/* <div
        style={{
          background: "#f5f7fa",
          border: "1px solid #e0e0e0",
          borderRadius: 6,
          padding: "10px 16px",
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: "0.9rem",
        }}
      >
        <div style={{ display: "flex", gap: 24 }}>
          <div>
            <strong>Bill No:</strong> 6666
          </div>

          <div>
            <strong>Bill Type:</strong> IPD
          </div>

          <div>
            <strong>Status:</strong> <Tag color="blue">In Progress</Tag>
          </div>
        </div>

        <div style={{ display: "flex", gap: 24 }}>
          <div>
            <strong>Start:</strong> 30 Mar 17:01
          </div>

          <div>
            <strong>Processing:</strong> 5m 4s
          </div>
        </div>
      </div> */}

      {isLoading ? (
        <div
          style={{
            margin: "auto",
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Spin />
        </div>
      ) : (
        <>
          {isReconBillAuditBlocked &&
            !billAuditSummaryGenerated &&
            reconBillAuditBlockMessage && (
              <div
                style={{
                  marginBottom: 16,
                  padding: "10px 12px",
                  border: "1px solid #ffccc7",
                  background: "#fff2f0",
                  color: "#cf1322",
                  borderRadius: 6,
                  fontWeight: 500,
                }}
              >
                {reconBillAuditBlockMessage}
              </div>
            )}
          {isTPA && (
            <div>
              <div
                style={{
                  textDecoration: "underline",
                  fontSize: "16px",
                  margin: "16px 0",
                  fontWeight: "bold",
                }}
              >
                Bill Details
              </div>
              <div>
                <div style={{ display: "flex", gap: 60, flexDirection: "row" }}>
                  <div style={{ flexDirection: "column" }}>
                    <p
                      style={{
                        marginBottom: "0.5rem",
                        // fontWeight: "600",
                        fontSize: "0.9rem",
                      }}
                    >
                      Hospital Room Type
                      {/* <span style={{ color: "red" }}> *</span> */}
                    </p>

                    <Select
                      style={{
                        width: "200px",
                      }}
                      placeholder="Select Hospital Room Type"
                      options={hospitalRoomTypeOptions}
                      value={
                        hospitalRoomTypeOptions?.some(
                          (option: any) =>
                            option?.value === selectedHospitalRoomType,
                        )
                          ? selectedHospitalRoomType
                          : null
                      }
                      onChange={(val) => {
                        handleHospitalRoomTypeChange(val);
                      }}
                      allowClear
                      status={hospitalRoomTypeError ? "error" : ""}
                      disabled={
                        preAuth?.portalData?.isBillAuditedAtleastOnce ||
                        isBillAuditActionBlocked
                      }
                    />

                    {/* {notMatchedError && (
                  <Tooltip title={notMatchedError}>
                    <span style={{ color: "red", fontSize: "1.2rem" }}>
                      {" "}
                      <ExclamationCircleOutlined />
                    </span>
                  </Tooltip>
                )} */}

                    {hospitalRoomTypeError && (
                      <div
                        style={{
                          color: "red",
                          fontSize: "0.9rem",
                          marginTop: 4,
                        }}
                      >
                        {hospitalRoomTypeError}
                      </div>
                    )}
                  </div>

                  <div>
                    <p
                      style={{
                        marginBottom: "0.5rem",
                        fontSize: "0.9rem",
                      }}
                    >
                      Occupied Room Type{" "}
                      <span style={{ fontWeight: "600" }}>
                        (PreAuth Approval Letter)
                      </span>
                    </p>
                    <Input
                      value={preAuth?.portalData?.occupiedRoomCategory || "-"}
                      disabled
                    />
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 60,
                    flexDirection: "row",
                    marginTop: "1rem",
                  }}
                >
                  <div style={{ flexDirection: "column" }}>
                    <p
                      style={{
                        marginBottom: "0.5rem",
                        // fontWeight: "600",
                        fontSize: "0.9rem",
                      }}
                    >
                      Scheme Room Type<span style={{ color: "red" }}> *</span>
                    </p>
                    <Select
                      style={{
                        width: "200px",
                      }}
                      placeholder="Select Room Type"
                      options={roomTypeOptions}
                      // value={selectedRoomType}
                      value={
                        roomTypeOptions?.some(
                          (option: any) => option?.value === selectedRoomType,
                        )
                          ? selectedRoomType
                          : null
                      }
                      onChange={(val) => {
                        handleRoomTypeChange(val);
                      }}
                      // disabled={isBillFiltersDisabled}
                      allowClear
                      status={roomTypeError ? "error" : ""}
                      disabled={
                        preAuth?.portalData?.isBillAuditedAtleastOnce ||
                        isBillAuditActionBlocked
                      }
                    />

                    {/* {notMatchedError && (
                  <Tooltip title={notMatchedError}>
                    <span style={{ color: "red", fontSize: "1.2rem" }}>
                      {" "}
                      <ExclamationCircleOutlined />
                    </span>
                  </Tooltip>
                )} */}

                    {roomTypeError && (
                      <div
                        style={{
                          color: "red",
                          fontSize: "0.9rem",
                          marginTop: 4,
                        }}
                      >
                        {roomTypeError}
                      </div>
                    )}
                  </div>
                  <div>
                    <p
                      style={{
                        marginBottom: "0.5rem",
                        fontSize: "0.9rem",
                      }}
                    >
                      Eligible Room Type{" "}
                      <span style={{ fontWeight: "600" }}>
                        (PreAuth Approval Letter)
                      </span>
                    </p>
                    <Input
                      value={preAuth?.portalData?.eligibleRoomCategory || "-"}
                      disabled
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          {documentTypes?.length > 0 && (
            <DocumentUploadBillAuditStage
              documentTypes={documentTypes}
              documentUrls={documentUrls}
              handleFileChange={handleFileChange}
              handleOpen={handleOpen}
              uploadFileList={uploadFileList}
              disabled={isBillAuditActionBlocked}
              selectedRoomType={selectedRoomType}
              selectedHospitalRoomType={selectedHospitalRoomType}
              isTPA={isTPA}
              billAuditStageStatus={isBillAuditIntimated}
              billSummaryLink={billSummaryLink}
              isFinalBillUploaded={isFinalBillUploaded}
              disableHospitalBillUpload={
                hasReconBillRows || isReconBillUploadLocked
              }
              hideDocumentUpload={hideDocumentUpload}
            />
          )}
        </>
      )}

      {hasReconBillRows &&
        refetchReconBillUploadRows &&
        onReconBillDocumentAttached &&
        onReconBillDocumentDeleted && (
          <ReconBillUploadContainer
            rows={reconBillUploadRows}
            dsRead={reconDsRead}
            loading={reconBillUploadLoading}
            refetch={refetchReconBillUploadRows}
            hospitalId={reconHospitalId}
            uploadDisabled={isReconBillUploadLocked}
            othersUploadDisabled={isOthersUploadLocked}
            isBillAuditIntimated={isBillAuditIntimated}
            documentTypes={documentTypes}
            documentUrls={documentUrls}
            handleOpen={handleOpen}
            onDocumentAttached={onReconBillDocumentAttached}
            onDocumentDeleted={onReconBillDocumentDeleted}
            schemeMetaDataName={schemeMetaDataName}
            visitType={visitType}
            onHasMismatchChange={setHasReconMismatch}
          />
        )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.8rem",
          marginTop: 16,
        }}
      >
        {/* {!disabled && !billAuditStageStatus && (
          <div style={{ width: "16%" }}>
            <Button
              type="primary"
              style={{
                backgroundColor: "rgb(0, 76, 143)",
                color: "#fff",
                boxShadow: "0px 2px 8px 0px #00000040",
                width: "100%",
              }}
              onClick={() => {
                // if (isTPA && !selectedHospitalRoomType) {
                //   setHospitalRoomTypeError("Hospital Room Type is required");
                //   errorMessage("Hospital Room Type is required");
                //   return;
                // }

                if (isTPA && !selectedRoomType) {
                  setRoomTypeError("Scheme Room Type is required");
                  errorMessageModal("Scheme Room Type is required");
                  return;
                }

                handleSaveData();
              }}
              loading={isUploading}
            >
              SAVE
            </Button>
          </div>
        )} */}

        {/* {documentUrls?.length > 0 && auth?.role?.role !== "FO_STAFF" ? (
          <div
            style={{
              display: "flex",
              gap: 10,
              alignItems: "center",
              marginRight: 10,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 10,
              }}
            >
              <Text strong>Bill Status</Text>
              <Tag
                color={getSseStatusColor(displayStatus)}
                style={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  padding: "6px 12px",
                  borderRadius: "6px",
                  letterSpacing: "0.4px",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                }}
              >
                {displayStatus?.toUpperCase()}
              </Tag>
            </div>

            <div
              style={{
                width: "1px",
                height: 40,
                background:
                  "linear-gradient(to bottom, transparent, #d9e1f2, transparent)",
              }}
            />
          </div>
        ) : (
          <></>
        )} */}

        {(isMidOfficeRole(auth?.role?.role) ||
        auth?.role?.role === "BO_STAFF" ||
          auth?.role?.role?.toUpperCase() === "ADMIN") &&
          (billAuditSummaryGenerated ||
            billAuditStageStatus ||
            isFinalBillStatus) && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginLeft: 100,
              }}
            >
              <Button
                type="primary"
                style={{
                  backgroundColor: "rgb(0, 76, 143)",
                  color: "#fff",
                  boxShadow: "0px 2px 8px 0px #00000040",
                  width: "100%",
                }}
                disabled={
                  isBillAuditActionBlocked ||
                  displayStatus?.toLowerCase() !== "success"
                }
                onClick={() =>
                  openBillAuditForMapping(
                    govtBillMappingId,
                    patients?.fetchBasicDetails?.casesResponse?.healthCareId,
                    "CGHS",
                  )
                }
              >
                Bill Audit
              </Button>

              {isFetchingBillDetails && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginLeft: 12,
                    whiteSpace: "nowrap",
                  }}
                >
                  <Spin size="small" />
                  <Text
                    type="secondary"
                    style={{
                      marginLeft: 8,
                      fontSize: 13,
                    }}
                  >
                    Fetching bill details...
                  </Text>
                </div>
              )}
            </div>
          )}

        {auth?.role?.role === "FO_STAFF" &&
          !billAuditStageStatus &&
          !hideSendToQc && (
            <div style={{ width: "16%" }}>
              <Button
                type="primary"
                style={{
                  backgroundColor: "rgb(0, 76, 143)",
                  color: "#fff",
                  boxShadow: "0px 2px 8px 0px #00000040",
                  width: "100%",
                }}
                disabled={isBillAuditActionBlocked || !canSendToQc}
                onClick={handleSendToQcClick}
              >
                Send To Qc
              </Button>
            </div>
          )}

        {auth?.role?.role === "FO_STAFF" &&
          isFinalBillPresent &&
          !isFinalBillStatus &&
          !hideSendToQc && (
            <div style={{ width: "16%" }}>
              <Button
                type="primary"
                style={{
                  backgroundColor: "rgb(0, 76, 143)",
                  color: "#fff",
                  boxShadow: "0px 2px 8px 0px #00000040",
                  width: "100%",
                }}
                disabled={isBillAuditActionBlocked || !canSendToQc}
                onClick={handleSendToQcClick}
              >
                Send To Qc
              </Button>
            </div>
          )}

        {documentUrls?.length > 0 && auth?.role?.role !== "FO_STAFF" ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginLeft: "20px",
              width: "20%",
            }}
          >
            {/* <Text strong style={{ fontSize: "1rem", color: "#374151" }}>Status:</Text> */}
            <Text
              style={{
                fontSize: "1rem",
                fontWeight: 600,
                padding: "8px 16px",
                borderRadius: "8px",
                letterSpacing: "0.4px",
                // boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                color: getSseStatusColor(displayStatus),
              }}
            >
              {readableStatus}
            </Text>
          </div>
        ) : (
          <></>
        )}
      </div>

      {shouldShowPayableBreakdown ? (
        <div style={{ marginTop: 20 }}>
          <div
            style={{
              textDecoration: "underline",
              fontSize: "16px",
              margin: "8px 0 16px",
              fontWeight: "bold",
            }}
          >
            Audit Breakdown
          </div>

          {payableBreakdownLoading ? (
            <div
              style={{
                minHeight: 120,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #f0f0f0",
                borderRadius: 12,
                background: "#fafcff",
              }}
            >
              <Spin />
            </div>
          ) : payableBreakdownError ? (
            <div
              style={{
                border: "1px solid #ffd6d6",
                background: "#fff2f0",
                borderRadius: 12,
                padding: 16,
                color: "#cf1322",
              }}
            >
              {payableBreakdownError}
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
                gap: 12,
              }}
            >
              {breakdownSummaryCards.map((card) => (
                <div
                  key={card.key}
                  onClick={() => setBreakdownModalKey(card.key)}
                  style={{
                    cursor: "pointer",
                    borderRadius: 10,
                    padding: "12px 14px",
                    border: "1px solid #d6e4ff",
                    background:
                      "linear-gradient(135deg, #f8fbff 0%, #eef5ff 100%)",
                    boxShadow: "0 4px 10px rgba(0, 76, 143, 0.08)",
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#004c8f",
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      marginBottom: 6,
                    }}
                  >
                    {card.title}
                  </div>
                  <div
                    style={{
                      fontSize: 20,
                      fontWeight: 700,
                      color: "#1f1f1f",
                      marginBottom: 4,
                    }}
                  >
                    {formatCurrencyAmount(card.amount)}
                  </div>
                  {/* <div style={{ fontSize: 12, color: "#595959" }}>
                    {card.subtitle}
                  </div>
                  <div
                    style={{
                      marginTop: 8,
                      fontSize: 12,
                      color: "#8c8c8c",
                    }}
                  >
                    {card.itemCount} item{card.itemCount === 1 ? "" : "s"}
                  </div> */}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}

      {shouldShowCpdReview ? (
        <div style={{ marginTop: 12 }}>
          <div
            style={{
              textDecoration: "underline",
              fontSize: "16px",
              margin: "4px 0 8px",
              fontWeight: "bold",
            }}
          >
            CPD Review
          </div>

          {cpdRemarksLoading ? (
            <div
              style={{
                minHeight: 56,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #f0f0f0",
                borderRadius: 10,
                background: "#fafcff",
              }}
            >
              <Spin />
            </div>
          ) : (
            <div
              style={{
                border: "1px solid #d6e4ff",
                borderRadius: 10,
                background: "linear-gradient(135deg, #f8fbff 0%, #eef5ff 100%)",
                boxShadow: "0 2px 6px rgba(0, 76, 143, 0.06)",
                padding: "10px 12px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  marginBottom: 6,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#004c8f",
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                  }}
                >
                  CPD Remarks
                </div>
                {typeof cpdRemarksData?.approved === "boolean" ? (
                  <Tag
                    color={cpdRemarksData.approved ? "green" : "red"}
                    style={{ margin: 0 }}
                  >
                    {cpdRemarksData.approved ? "Approved" : "Not Approved"}
                  </Tag>
                ) : null}
              </div>
              <div
                style={{
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.4,
                  color: "#262626",
                  fontSize: 13,
                  background: "#fff",
                  border: "1px solid #e6f0ff",
                  borderRadius: 6,
                  padding: "8px 10px",
                }}
              >
                {cpdRemarksText.replace(/\n{2,}/g, "\n")}
              </div>
            </div>
          )}
        </div>
      ) : null}

      <Modal
        title={breakdownModalKey ? breakdownCardLabels[breakdownModalKey] : ""}
        open={!!breakdownModalKey}
        onCancel={() => {
          setBreakdownModalKey(null);
          setBreakdownPagination({ current: 1, pageSize: 10 });
        }}
        footer={null}
        width="100vw"
        style={{
          top: 10,
          left: 4,
          padding: 0,
          maxWidth: "100vw",
          height: "150vh",
        }}
        bodyStyle={{
          height: "calc(150vh - 55px)",
          overflow: "auto",
          padding: 0,
        }}
        wrapClassName="management-age-stage-table-modal"
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
            gap: 16,
          }}
        >
          <div style={{ color: "#595959" }}>
            {breakdownModalKey === "hospitalTotal"
              ? "Showing all bill items included in the hospital total."
              : breakdownModalKey === "cghsTotal"
                ? "Showing remaining items contributing to the CGHS total."
                : breakdownModalKey === "patientPayableTotal"
                  ? "Showing all patient payable items."
                  : "Showing all non-payable items."}
          </div>
          <div style={{ fontWeight: 700, color: "#004c8f" }}>
            Total:{" "}
            {breakdownModalKey
              ? formatCurrencyAmount(
                  payableBreakdown?.totals?.[breakdownModalKey] || 0,
                )
              : "0.00"}
          </div>
        </div>

        <Table
          rowKey={(record, index) =>
            `${record.billNo || "bill"}-${record.date || "date"}-${index}`
          }
          className="operational-dashboard-table"
          columns={breakdownColumns}
          dataSource={paginatedBreakdownItems}
          pagination={{
            current: breakdownPagination.current,
            pageSize: breakdownPagination.pageSize,
            total: activeBreakdownItems.length,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            onChange: (page, pageSize) =>
              setBreakdownPagination({
                current: page,
                pageSize: pageSize || 10,
              }),
          }}
          rowClassName={(_, index) =>
            index % 2 === 0 ? "ant-table-row-light" : ""
          }
          // scroll={{ x: 1650, y: "calc(100vh - 260px)" }}
          scroll={{
            x: 150,
            y: scrollY,
          }}
          locale={{
            emptyText: (
              <Empty description="No breakdown items available for this total" />
            ),
          }}
          size="middle"
        />
      </Modal>
    </div>
  );
};

export default BillAuditStage;
